(() => {
  'use strict';

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(value);
  };

  const text = (root, selector, fallback = '') => {
    const node = root.querySelector(selector);
    return node ? (node.textContent || '').trim() : fallback;
  };

  class MomentsProjectStore extends EventTarget {
    constructor(root) {
      super();
      this.root = root;
      this.mode = 'single';
      this.state = {
        version: '6.0.19',
        orderMode: 'single',
        product: {
          title: root.dataset.meProductTitle || '',
          handle: root.dataset.meProductHandle || '',
          variantId: '',
          singleSizeVariantId: '',
          color: '',
          size: '',
          selectedColorProduct: null,
          distributionVariantIds: {}
        },
        sizeDistribution: {},
        quantity: 1,
        locations: [],
        pricing: {
          unitCents: Number(root.dataset.meBasePriceCents || 0),
          totalCents: Number(root.dataset.meBasePriceCents || 0)
        },
        status: 'in_progress',
        updatedAt: new Date().toISOString()
      };
      this.cache();
      this.hydrateDistribution();
      this.cache();
      this.bind();
      this.setMode('single', false);
      this.refresh('initial');
      window.MomentsProject = this;
    }

    cache() {
      this.modeButtons = [...this.root.querySelectorAll('[data-me-order-mode]')];
      this.singlePanel = this.root.querySelector('[data-me-single-size-panel]');
      this.distributionPanels = [...this.root.querySelectorAll('[data-me-size-distribution]')];
      this.distributionPanel = this.distributionPanels[0] || null;
      this.distributionInputs = [...this.root.querySelectorAll('[data-me-size-quantity]')];
      this.distributionTotal = this.root.querySelector('[data-me-distribution-total]');
      this.distributionTotalMirrors = [...this.root.querySelectorAll('[data-me-distribution-total-mirror]')];
      this.distributionMessage = this.root.querySelector('[data-me-distribution-message]');
      this.orderTypeProperty = this.root.querySelector('[data-me-order-type-property]');
      this.distributionProperty = this.root.querySelector('[data-me-size-distribution-property]');
      this.sizeProperty = this.root.querySelector('[data-me-size-property]');
      this.quantityInput = this.root.querySelector('[data-me-quantity-input], input[name="quantity"]');
      this.reviewSubmit = this.root.querySelector('[data-me-review-submit]');
      this.singleReviewQuantity = this.root.querySelector('[data-me-single-review-quantity]');
      this.cartNote = this.root.querySelector('[data-me-multi-size-cart-note]');
      this.importButton = this.root.querySelector('[data-me-size-import-button]');
      this.importInput = this.root.querySelector('[data-me-size-import-input]');
      this.importStatus = this.root.querySelector('[data-me-size-import-status]');
      this.importProperty = this.root.querySelector('[data-me-size-import-property]');
      this.templateButton = this.root.querySelector('[data-me-size-template-button]');
      if (typeof this.confirmedColor !== 'string') this.confirmedColor = '';
      if (!this.colorSyncTimer) this.colorSyncTimer = null;
    }

    getProductData() {
      // Color swatches can load a different Shopify product. Always prefer the
      // currently confirmed color product over the initial page product.
      if (window.MomentsCurrentProduct && Array.isArray(window.MomentsCurrentProduct.variants)) {
        return window.MomentsCurrentProduct;
      }
      const jsonNode = this.root.querySelector('[data-me-initial-product-json]');
      if (!jsonNode) return null;
      try {
        return JSON.parse(jsonNode.textContent || '{}');
      } catch (error) {
        console.warn('[Moments Project Builder] Product JSON could not be parsed.', error);
        return null;
      }
    }

    findOptionIndex(product, keyword) {
      const options = Array.isArray(product?.options) ? product.options : [];
      return options.findIndex((option) => {
        const name = String(typeof option === 'string' ? option : option?.name || '').toLowerCase().trim();
        return name.includes(keyword);
      });
    }


    normalizeSize(value) {
      const normalized = String(value || '')
        .trim()
        .toUpperCase()
        .replace(/[._-]+/g, ' ')
        .replace(/\b(YOUTH|ADULT|WOMEN|WOMENS|MEN|MENS|UNISEX|SIZE|SIZES)\b/g, ' ')
        .replace(/\s+/g, '')
        .replace(/^EXTRASMALL$/, 'XS')
        .replace(/^SMALL$/, 'S')
        .replace(/^MEDIUM$/, 'M')
        .replace(/^LARGE$/, 'L')
        .replace(/^EXTRALARGE$/, 'XL')
        .replace(/^([2-9])X$/, '$1XL')
        .replace(/^([2-9])XLARGE$/, '$1XL');
      const aliases = { YXS:'XS', YS:'S', YM:'M', YL:'L', YXL:'XL', YOUTHXS:'XS', YOUTHS:'S', YOUTHM:'M', YOUTHL:'L', YOUTHXL:'XL' };
      return aliases[normalized] || normalized;
    }

    variantOptionValues(variant) {
      const values = Array.isArray(variant?.options) ? [...variant.options] : [];
      ['option1', 'option2', 'option3'].forEach((key) => {
        if (variant?.[key] != null && !values.includes(variant[key])) values.push(variant[key]);
      });
      if (variant?.title && variant.title !== 'Default Title') {
        String(variant.title).split(' / ').forEach((value) => values.push(value));
      }
      return values.map((value) => String(value || '').trim()).filter(Boolean);
    }

    resolveVariantForSize(product, size) {
      if (!product || !Array.isArray(product.variants)) return null;
      const normalizedSize = this.normalizeSize(size);
      const sizeIndex = this.findOptionIndex(product, 'size');
      const variants = product.variants.filter((variant) => variant && variant.id);

      const optionAt = (variant, index) => variant?.options?.[index] ?? variant?.[`option${index + 1}`] ?? '';
      if (sizeIndex >= 0) {
        const direct = variants.find((variant) => this.normalizeSize(optionAt(variant, sizeIndex)) === normalizedSize);
        if (direct) return direct;
      }

      const valueMatch = variants.find((variant) =>
        this.variantOptionValues(variant).some((value) => this.normalizeSize(value) === normalizedSize)
      );
      if (valueMatch) return valueMatch;

      const titleMatch = variants.find((variant) => String(variant.title || '')
        .split(/\s*\/\s*|,|\|/)
        .some((value) => this.normalizeSize(value) === normalizedSize));
      if (titleMatch) return titleMatch;

      // Final safe fallback for supplier products with one size option whose
      // product JSON omits option labels: align the visible size order to the
      // Shopify variant order.
      const visibleSizes = [...this.root.querySelectorAll('[data-me-size-button]')]
        .map((button) => this.normalizeSize(button.dataset.sizeValue || button.dataset.meSize || button.textContent))
        .filter(Boolean);
      const index = visibleSizes.indexOf(normalizedSize);
      return index >= 0 ? variants[index] || null : null;
    }

    buildDistributionVariantMap(product = this.getProductData()) {
      const map = {};
      const visibleSizes = this.getDistributionSizes();
      visibleSizes.forEach((size) => {
        const variant = this.resolveVariantForSize(product, size);
        if (variant?.id) map[this.normalizeSize(size)] = Number(variant.id);
      });
      window.MomentsDistributionVariantMap = map;
      return map;
    }

    getDistributionSizes() {
      const fromButtons = [...this.root.querySelectorAll('[data-me-size-button]')]
        .map((button) => (button.dataset.sizeValue || button.dataset.meSize || button.textContent || '').trim())
        .filter(Boolean);

      const product = this.getProductData();
      if (!product || !Array.isArray(product.variants)) return [...new Set(fromButtons)];

      const sizeIndex = this.findOptionIndex(product, 'size');
      if (sizeIndex < 0) return [...new Set(fromButtons)];

      const colorIndex = this.findOptionIndex(product, 'color');
      const selectedColor = this.readColor().toLowerCase().trim();
      const availableVariants = product.variants.filter((variant) => variant.available !== false);
      let relevant = availableVariants;

      if (colorIndex >= 0 && selectedColor) {
        const colorMatches = availableVariants.filter((variant) => {
          const value = String(variant.options?.[colorIndex] || '').toLowerCase().trim();
          return value === selectedColor;
        });
        if (colorMatches.length) relevant = colorMatches;
      }

      const sizes = relevant
        .map((variant) => String(variant.options?.[sizeIndex] || '').trim())
        .filter(Boolean);

      return [...new Set(sizes.length ? sizes : fromButtons)];
    }

    hydrateDistribution(force = false) {
      const grid = this.root.querySelector('[data-me-size-distribution-grid], .me-size-distribution__grid');
      if (!grid) return;
      const existing = [...grid.querySelectorAll('[data-me-size-quantity]')];
      if (existing.length && !force) return;

      const preserved = {};
      existing.forEach((input) => {
        preserved[input.dataset.meSizeValue || ''] = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
      });

      const sizes = this.getDistributionSizes();
      grid.replaceChildren();

      if (!sizes.length) {
        const empty = document.createElement('p');
        empty.className = 'me-size-distribution__empty';
        empty.textContent = 'No available size variants were found for this product.';
        grid.appendChild(empty);
        return;
      }

      sizes.forEach((size) => {
        const row = document.createElement('div');
        row.className = 'me-size-distribution__row';
        row.dataset.meDistributionRow = '';

        const label = document.createElement('span');
        label.className = 'me-size-distribution__size';
        label.textContent = size;

        const control = document.createElement('div');
        control.className = 'me-size-distribution__control';

        const minus = document.createElement('button');
        minus.type = 'button';
        minus.className = 'me-size-distribution__step';
        minus.dataset.meDistributionMinus = '';
        minus.setAttribute('aria-label', `Decrease quantity for size ${size}`);
        minus.textContent = '−';

        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.inputMode = 'numeric';
        input.value = String(preserved[size] || 0);
        input.dataset.meSizeQuantity = '';
        input.dataset.meSizeValue = size;
        const product = this.getProductData();
        const variantMap = this.buildDistributionVariantMap(product);
        const resolvedVariant = this.resolveVariantForSize(product, size);
        const mappedId = variantMap[this.normalizeSize(size)] || resolvedVariant?.id;
        if (mappedId) input.dataset.meVariantId = String(mappedId);
        input.setAttribute('aria-label', `Quantity for size ${size}`);

        const plus = document.createElement('button');
        plus.type = 'button';
        plus.className = 'me-size-distribution__step';
        plus.dataset.meDistributionPlus = '';
        plus.setAttribute('aria-label', `Increase quantity for size ${size}`);
        plus.textContent = '+';

        control.append(minus, input, plus);
        row.append(label, control);
        grid.appendChild(row);
      });
    }

    adjustDistribution(button, direction) {
      const row = button.closest('[data-me-distribution-row]');
      const input = row?.querySelector('[data-me-size-quantity]');
      if (!input) return;
      const current = Math.max(0, Number.parseInt(input.value || '0', 10) || 0);
      input.value = String(Math.max(0, current + direction));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    }

    bind() {
      const refresh = (event) => this.refresh(event?.type || 'change');

      this.root.addEventListener('input', (event) => {
        if (event.target.matches('[data-me-size-quantity]')) {
          this.handleDistributionInput(event.target, false);
          return;
        }
        if (event.target.closest('[data-me-designer-color], [data-me-color-button], [data-me-designer-color-swatches]')) return;
        if (this.syncingQuantity && event.target === this.quantityInput) return;
        refresh(event);
      });

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-me-size-quantity]')) {
          this.handleDistributionInput(event.target, true);
          return;
        }
        if (event.target.closest('[data-me-designer-color], [data-me-color-button], [data-me-designer-color-swatches]')) return;
        if (this.syncingQuantity && event.target === this.quantityInput) return;
        refresh(event);
      });

      this.root.addEventListener('blur', (event) => {
        if (!event.target.matches('[data-me-size-quantity]')) return;
        this.handleDistributionInput(event.target, true);
      }, true);

      this.root.addEventListener('click', (event) => {
        const modeButton = event.target.closest('[data-me-order-mode]');
        if (modeButton) {
          this.setMode(modeButton.dataset.meOrderMode || 'single');
          return;
        }
        const distributionPlus = event.target.closest('[data-me-distribution-plus]');
        if (distributionPlus) {
          this.adjustDistribution(distributionPlus, 1);
          return;
        }
        const distributionMinus = event.target.closest('[data-me-distribution-minus]');
        if (distributionMinus) {
          this.adjustDistribution(distributionMinus, -1);
          return;
        }
        if (event.target.closest('[data-me-size-import-button]')) {
          this.importInput?.click();
          return;
        }
        if (event.target.closest('[data-me-size-template-button]')) {
          this.downloadSizeTemplate();
          return;
        }
        if (event.target.closest('[data-me-size-button], [data-me-placement], [data-me-quantity-plus], [data-me-quantity-minus], [data-me-designer-primary-action]')) {
          window.setTimeout(() => this.refresh('interaction'), 0);
        }
      });

      this.importInput?.addEventListener('change', (event) => {
        const file = event.target.files?.[0];
        if (file) this.importSizeCsv(file);
        event.target.value = '';
      });

      document.addEventListener('me:designer:updated', refresh);
      document.addEventListener('me:price:updated', refresh);
      window.addEventListener('me:variant-selection-state', () => {
        window.setTimeout(() => this.refresh('variant-selection'), 0);
      });
      window.addEventListener('me:designer-color-changed', (event) => {
        const detail = event.detail || {};
        this.confirmedColor = String(detail.colorName || '').trim();
        window.clearTimeout(this.colorSyncTimer);
        this.colorSyncTimer = window.setTimeout(() => {
          // The apparel color controller is the single owner of product media.
          // Project Builder consumes the confirmed product for size/variant mapping,
          // but must never dispatch a second media event that can overwrite the
          // correct catalog image with a stale product-level featured image.
          if (detail.product) {
            window.MomentsCurrentProduct = detail.product;
            this.state.product.selectedColorProduct = detail.product;
            this.state.product.distributionVariantIds = this.buildDistributionVariantMap(detail.product);
          }
          if (this.mode === 'multi') {
            this.hydrateDistribution(true);
            this.distributionInputs = [...this.root.querySelectorAll('[data-me-size-quantity]')];
          }
          this.refresh('color-change');
        }, 20);
      });
    }

    mediaUrl(value) {
      if (!value) return '';
      if (typeof value === 'string') return value;
      return value.src || value.url || value.preview_image?.src || '';
    }

    forceSelectedColorMedia(product, colorName) {
      // Deprecated in v6.0.12. The unified apparel color controller owns media.
      // Retained as a no-op for compatibility with older extension code.
      return;
      const images = Array.isArray(product.images) ? product.images.map((image) => this.mediaUrl(image)).filter(Boolean) : [];
      const colorIndex = this.findOptionIndex(product, 'color');
      const normalized = String(colorName || '').trim().toLowerCase();
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const matchingVariant = variants.find((variant) => {
        if (colorIndex < 0 || !normalized) return false;
        return String(variant.options?.[colorIndex] || '').trim().toLowerCase() === normalized;
      });
      const frontImage = this.mediaUrl(matchingVariant?.featured_image || matchingVariant?.featured_media || matchingVariant?.image) || this.mediaUrl(product.featured_image) || images[0] || '';
      if (!frontImage) return;
      window.dispatchEvent(new CustomEvent('me:product-media-changed', {
        detail: {
          frontImage,
          backImage: images.find((image) => /back/i.test(image)) || images[1] || frontImage,
          leftSleeveImage: images.find((image) => /left[-_ ]?sleeve/i.test(image)) || images[2] || frontImage,
          rightSleeveImage: images.find((image) => /right[-_ ]?sleeve/i.test(image)) || images[3] || frontImage,
          colorName,
          variantId: matchingVariant?.id || null
        }
      }));
    }

    normalizeSizeToken(value) {
      return String(value || '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, '')
        .replace(/^([2-9])X$/, '$1XL')
        .replace(/^([2-9])X-LARGE$/, '$1XL');
    }

    parseCsv(textValue) {
      const rows = [];
      let row = [];
      let field = '';
      let quoted = false;
      const source = String(textValue || '').replace(/^\uFEFF/, '');

      for (let index = 0; index < source.length; index += 1) {
        const character = source[index];
        if (character === '"') {
          if (quoted && source[index + 1] === '"') {
            field += '"';
            index += 1;
          } else {
            quoted = !quoted;
          }
        } else if (character === ',' && !quoted) {
          row.push(field.trim());
          field = '';
        } else if ((character === '\n' || character === '\r') && !quoted) {
          if (character === '\r' && source[index + 1] === '\n') index += 1;
          row.push(field.trim());
          field = '';
          if (row.some((value) => value !== '')) rows.push(row);
          row = [];
        } else {
          field += character;
        }
      }
      row.push(field.trim());
      if (row.some((value) => value !== '')) rows.push(row);
      return rows;
    }

    setImportStatus(message, type = '') {
      if (!this.importStatus) return;
      this.importStatus.textContent = message;
      this.importStatus.classList.toggle('is-success', type === 'success');
      this.importStatus.classList.toggle('is-error', type === 'error');
    }

    downloadSizeTemplate() {
      const content = 'Name,Size,Quantity\nAlex,L,1\nJordan,XL,1\nTeam extras,M,10\n';
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${this.state.product.handle || 'moments-size-list'}-template.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);

      /* v15.4.7.9 — confirm the template download without increasing the
         Step 4 card height. The aria-live status remains available to
         assistive technology while the visible confirmation temporarily
         replaces the button label. */
      const importWrap = this.templateButton?.closest('[data-me-size-import]');
      if (this.templateStatusTimer) window.clearTimeout(this.templateStatusTimer);
      importWrap?.classList.add('is-template-confirmation');
      if (this.templateButton) {
        this.templateButton.textContent = 'Downloaded ✓';
        this.templateButton.classList.add('is-downloaded');
      }
      this.setImportStatus('Size template downloaded successfully.', 'success');

      this.templateStatusTimer = window.setTimeout(() => {
        importWrap?.classList.remove('is-template-confirmation');
        if (this.templateButton) {
          this.templateButton.textContent = 'Download template';
          this.templateButton.classList.remove('is-downloaded');
        }
        if (this.importStatus) {
          this.importStatus.textContent = '';
          this.importStatus.classList.remove('is-success', 'is-error');
        }
        this.templateStatusTimer = null;
      }, 2200);
    }

    async importSizeCsv(file) {
      if (!file || !/\.csv$/i.test(file.name || '')) {
        this.setImportStatus('Choose a CSV file to import.', 'error');
        return;
      }
      try {
        const source = await file.text();
        const rows = this.parseCsv(source);
        if (rows.length < 2) throw new Error('The CSV needs a header row and at least one data row.');

        const headers = rows[0].map((value) => String(value).trim().toLowerCase().replace(/[^a-z0-9]+/g, ''));
        const sizeIndex = headers.findIndex((value) => ['size', 'shirtsize', 'garmentsize', 'apparelsize'].includes(value));
        const quantityIndex = headers.findIndex((value) => ['quantity', 'qty', 'count', 'pieces'].includes(value));
        if (sizeIndex < 0) throw new Error('Add a column named Size to the CSV.');

        const available = new Map();
        this.distributionInputs.forEach((input) => {
          available.set(this.normalizeSizeToken(input.dataset.meSizeValue), input);
        });

        const totals = new Map();
        const unmatched = new Set();
        let importedRows = 0;
        rows.slice(1).forEach((columns) => {
          const rawSize = columns[sizeIndex] || '';
          if (!String(rawSize).trim()) return;
          const normalized = this.normalizeSizeToken(rawSize);
          const quantity = quantityIndex >= 0 ? this.sanitizeDistributionValue(columns[quantityIndex] || '1') : 1;
          if (quantity < 1) return;
          importedRows += 1;
          if (!available.has(normalized)) {
            unmatched.add(String(rawSize).trim());
            return;
          }
          totals.set(normalized, (totals.get(normalized) || 0) + quantity);
        });

        if (!importedRows) throw new Error('No usable size rows were found in the CSV.');
        if (!totals.size) throw new Error(`None of the imported sizes match this garment${unmatched.size ? `: ${[...unmatched].join(', ')}` : '.'}`);

        this.distributionInputs.forEach((input) => {
          input.value = String(totals.get(this.normalizeSizeToken(input.dataset.meSizeValue)) || 0);
        });
        if (this.importProperty) this.importProperty.value = `${file.name} — ${importedRows} row${importedRows === 1 ? '' : 's'}`;
        this.refresh('csv-import');

        const totalPieces = [...totals.values()].reduce((sum, value) => sum + value, 0);
        const warning = unmatched.size ? ` ${unmatched.size} unmatched size${unmatched.size === 1 ? '' : 's'} ignored: ${[...unmatched].join(', ')}.` : '';
        this.setImportStatus(`Imported ${totalPieces} piece${totalPieces === 1 ? '' : 's'} from ${file.name}.${warning}`, unmatched.size ? '' : 'success');
      } catch (error) {
        console.warn('[Moments Project Builder] CSV import failed.', error);
        this.setImportStatus(error?.message || 'The CSV could not be imported.', 'error');
      }
    }

    setMode(mode, announce = true) {
      this.mode = mode === 'multi' ? 'multi' : 'single';
      this.modeButtons.forEach((button) => {
        const active = button.dataset.meOrderMode === this.mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-checked', String(active));
      });
      if (this.singlePanel) this.singlePanel.hidden = this.mode === 'multi';
      this.distributionPanels.forEach((panel) => { panel.hidden = this.mode !== 'multi'; });
      if (this.singleReviewQuantity) this.singleReviewQuantity.hidden = this.mode === 'multi';
      if (this.orderTypeProperty) this.orderTypeProperty.value = this.mode === 'multi' ? 'Multiple Sizes' : 'Single Size';
      if (this.cartNote) this.cartNote.hidden = this.mode !== 'multi';
      if (this.reviewSubmit && this.mode !== 'multi') {
        this.reviewSubmit.removeAttribute('aria-disabled');
      }

      if (this.mode === 'multi') {
        if (this.sizeProperty) this.sizeProperty.value = '';
        this.state.product.singleSizeVariantId = '';
        this.hydrateDistribution();
        this.distributionInputs = [...this.root.querySelectorAll('[data-me-size-quantity]')];
        this.syncDistribution();
      } else {
        if (this.distributionProperty) this.distributionProperty.value = '';
        if (this.quantityInput && Number(this.quantityInput.value || 0) < 1) this.quantityInput.value = '1';
      }
      this.refresh(announce ? 'order-mode' : 'initial-mode');
    }

    sanitizeDistributionValue(value) {
      const digits = String(value ?? '').replace(/[^0-9]/g, '');
      if (!digits) return 0;
      return Math.max(0, Number.parseInt(digits, 10) || 0);
    }

    handleDistributionInput(input, commit = false) {
      if (!input) return;

      const raw = String(input.value ?? '');
      const cleaned = raw.replace(/[^0-9]/g, '');

      // While typing, allow a temporarily blank field so customers can replace
      // values naturally without the control forcing 0 after every keystroke.
      if (raw !== cleaned) input.value = cleaned;
      if (commit && input.value === '') input.value = '0';

      this.refresh(commit ? 'distribution-commit' : 'distribution-input');
    }

    readDistribution({ commit = false } = {}) {
      const distribution = {};
      this.distributionInputs.forEach((input) => {
        const quantity = this.sanitizeDistributionValue(input.value);
        if (commit && input.value === '') input.value = '0';
        if (quantity > 0) distribution[input.dataset.meSizeValue || 'Size'] = quantity;
      });
      return distribution;
    }

    syncDistribution({ commit = false } = {}) {
      const distribution = this.readDistribution({ commit });
      const total = Object.values(distribution).reduce((sum, value) => sum + value, 0);
      const summary = Object.entries(distribution).map(([size, quantity]) => `${size}: ${quantity}`).join(', ');
      if (this.distributionTotal) this.distributionTotal.textContent = String(total);
      this.distributionTotalMirrors.forEach((node) => { node.textContent = String(total); });
      if (this.distributionMessage) {
        this.distributionMessage.textContent = total > 0 ? `${total} total piece${total === 1 ? '' : 's'} across ${Object.keys(distribution).length} size${Object.keys(distribution).length === 1 ? '' : 's'}.` : 'Enter at least one size quantity to continue.';
        this.distributionMessage.classList.toggle('is-complete', total > 0);
      }
      if (this.distributionProperty) this.distributionProperty.value = summary;
      // Do not write "Multiple Sizes" into the single-size property. That
      // value was previously interpreted as an actual Shopify size and made a
      // color click satisfy single-size validation.
      if (this.sizeProperty && this.mode === 'multi') this.sizeProperty.value = '';
      if (this.quantityInput) {
        const nextQuantity = String(total || 1);
        if (this.quantityInput.value !== nextQuantity) {
          this.syncingQuantity = true;
          this.quantityInput.value = nextQuantity;
          // Dispatch only change here. The theme's color/variant preview logic
          // listens broadly for input events; emitting a synthetic input while
          // editing a size distribution can reset the active garment preview.
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncingQuantity = false;
        }
      }
      return { distribution, total, summary };
    }

    readQuantity() {
      if (this.mode === 'multi') return this.syncDistribution().total;
      const value = Number.parseInt(this.quantityInput?.value || '1', 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    readSize() {
      if (this.mode === 'multi') {
        const { summary } = this.syncDistribution();
        return summary || '';
      }
      const active = this.root.querySelector('[data-me-size-button].is-active, [data-me-size-button][aria-pressed="true"], input[name*="Size"]:checked');
      if (!active) return '';
      return (active.dataset.meSize || active.dataset.sizeValue || active.value || active.textContent || '').trim();
    }

    readColor() {
      if (this.confirmedColor) return this.confirmedColor;
      const selected = this.root.querySelector('[data-me-designer-color].is-active, [data-me-designer-color][aria-pressed="true"], [data-me-color-button].is-active, [data-me-color-button][aria-pressed="true"], input[name*="Color"]:checked');
      if (selected) return (selected.dataset.colorName || selected.dataset.meColor || selected.value || selected.getAttribute('aria-label') || selected.textContent || '').trim();
      const label = text(this.root, '[data-me-selected-color]');
      return label.replace(/^Selected:\s*/i, '').trim();
    }

    readLocations() {
      const checked = [...this.root.querySelectorAll('input[type="checkbox"][data-me-placement]:checked, input[type="checkbox"][name*="properties"]:checked')];
      const labels = checked.map((input) => {
        const label = input.closest('label');
        return (input.dataset.mePlacement || input.value || label?.textContent || '').trim();
      }).filter(Boolean);
      if (labels.length) return [...new Set(labels)];
      return [...this.root.querySelectorAll('[data-me-location-navigator] button, [data-me-location-navigator] [role="button"]')]
        .map((node) => (node.dataset.meLocation || node.textContent || '').trim())
        .filter(Boolean);
    }

    readUnitCents() {
      const unit = text(this.root, '[data-me-price-unit]');
      if (unit) {
        const parsed = Number(unit.replace(/[^0-9.-]/g, ''));
        if (Number.isFinite(parsed)) return Math.round(parsed * 100);
      }
      return Number(this.root.dataset.meBasePriceCents || 0);
    }

    readTotalCents(quantity, unitCents) {
      const total = text(this.root, '[data-me-price-total]');
      if (total) {
        const parsed = Number(total.replace(/[^0-9.-]/g, ''));
        if (Number.isFinite(parsed)) return Math.round(parsed * 100);
      }
      return quantity * unitCents;
    }

    refresh(reason = 'update') {
      const commitDistribution = reason === 'distribution-commit';
      const distributionState = this.mode === 'multi' ? this.syncDistribution({ commit: commitDistribution }) : { distribution: {}, total: this.readQuantity() };
      const quantity = this.mode === 'multi' ? distributionState.total : this.readQuantity();
      const unitCents = this.readUnitCents();
      this.state = {
        ...this.state,
        orderMode: this.mode,
        product: {
          ...this.state.product,
          variantId: this.mode === 'single' ? (this.root.querySelector('[data-me-variant-id]')?.value || '') : '',
          singleSizeVariantId: this.mode === 'single' ? (this.root.querySelector('[data-me-variant-id]')?.value || '') : '',
          color: this.readColor(),
          size: this.mode === 'multi' ? '' : this.readSize(),
          selectedColorProduct: window.MomentsCurrentProduct || this.state.product.selectedColorProduct || null,
          distributionVariantIds: this.mode === 'multi' ? this.buildDistributionVariantMap() : {}
        },
        sizeDistribution: distributionState.distribution,
        quantity,
        locations: this.readLocations(),
        pricing: { unitCents, totalCents: this.readTotalCents(quantity, unitCents) },
        updatedAt: new Date().toISOString()
      };
      this.render();
      this.dispatchEvent(new CustomEvent('update', { detail: { state: this.state, reason } }));
      window.MomentsProjectState = this.state;
      document.dispatchEvent(new CustomEvent('me:project:update', { detail: { state: this.state, reason } }));
    }

    render() {
      const set = (selector, value) => { const node = this.root.querySelector(selector); if (node) node.textContent = value; };
      set('[data-me-project-color]', this.state.product.color || 'Not selected');
      const distributionSummary = Object.entries(this.state.sizeDistribution || {}).map(([size, quantity]) => `${size}: ${quantity}`).join(', ');
      set('[data-me-project-size]', this.mode === 'multi' ? (distributionSummary || 'Add size quantities') : (this.state.product.size || 'Not selected'));
      set('[data-me-project-quantity]', String(this.state.quantity));
      set('[data-me-project-locations]', this.state.locations.length ? this.state.locations.join(', ') : 'None selected');
      set('[data-me-project-total]', money(this.state.pricing.totalCents));
      set('[data-me-project-status]', this.mode === 'multi' && this.state.quantity > 0 ? 'Distribution ready' : (this.state.locations.length ? 'Configured' : 'In progress'));
    }

    getState() {
      return typeof structuredClone === 'function' ? structuredClone(this.state) : JSON.parse(JSON.stringify(this.state));
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-me-designer]').forEach((root) => {
      if (!root.dataset.meProjectBuilderReady) {
        root.dataset.meProjectBuilderReady = 'true';
        new MomentsProjectStore(root);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
